import type { NotificationDropdownProps } from '@/types/notification'
import { CheckCheck, ExternalLink } from 'lucide-react'
import * as React from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { NotificationBell } from '@/components/ui/notification-bell'
import { NotificationItem } from '@/components/ui/notification-item'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export function NotificationDropdown({
  notifications,
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onViewAll,
  onOpen,
  loading = false,
  error = null,
  className,
}: NotificationDropdownProps) {
  const recentNotifications = notifications.slice(0, 5)
  const hasNotifications = notifications.length > 0

  return (
    <DropdownMenu onOpenChange={(open) => {
      if (open)
        onOpen?.()
    }}
    >
      <DropdownMenuTrigger asChild>
        <NotificationBell unreadCount={unreadCount} className={className} />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[380px]">
        <div className="pb-2">
          <div className="flex items-center justify-between">
            <DropdownMenuLabel className="text-base font-semibold">Notifications</DropdownMenuLabel>
            {unreadCount > 0 && (
              <span className="text-xs text-muted-foreground pr-2">
                {unreadCount}
                {' '}
                unread
              </span>
            )}
          </div>
        </div>

        <DropdownMenuSeparator />

        {loading
          ? (
              <div className="p-4 space-y-3">
                {[...Array.from({ length: 3 })].map((_, i) => (
                  <div key={i} className="flex gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            )
          : error
            ? (
                <div className="p-8 text-center">
                  <p className="text-sm text-muted-foreground">Failed to load notifications</p>
                  <p className="text-xs text-muted-foreground mt-1">{error.message}</p>
                </div>
              )
            : !hasNotifications
                ? (
                    <div className="p-8 text-center">
                      <p className="text-sm text-muted-foreground">No notifications yet</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        We'll notify you when something important happens
                      </p>
                    </div>
                  )
                : (
                    <>
                      <ScrollArea className="h-[300px]">
                        <div className="flex flex-col" role="list">
                          {recentNotifications.map(notification => (
                            <React.Fragment key={notification.id}>
                              <NotificationItem
                                notification={notification}
                                onMarkAsRead={onMarkAsRead}
                                showUnreadAction={false}
                                className="border-b last:border-b-0"
                              />
                            </React.Fragment>
                          ))}
                        </div>
                      </ScrollArea>

                      {notifications.length > 5 && (
                        <>
                          <DropdownMenuSeparator />
                          <div className="p-2 text-center">
                            <span className="text-xs text-muted-foreground">
                              {notifications.length - 5}
                              {' '}
                              more notifications
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}

        <DropdownMenuSeparator />

        <div className="p-2 flex gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" className="flex-1" onClick={onMarkAllAsRead}>
              <CheckCheck className="mr-2 h-4 w-4" />
              Mark all as read
            </Button>
          )}

          <Button
            variant={unreadCount > 0 ? 'default' : 'outline'}
            size="sm"
            className={cn(unreadCount > 0 ? 'flex-1' : 'w-full')}
            onClick={onViewAll}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            View all
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
